/**
 * Creates an empty 5x5 bingo board initialized with zeros.
 * @returns {number[][]} A 5x5 array representing an empty bingo board.
 */
const createEmptyBoard = () => Array(5).fill(null).map(() => Array(5).fill(0));

// --- Standard Bingo Patterns (can be reused) ---

/**
 * Generates individual patterns for horizontal, vertical, and diagonal straight lines.
 * @returns {Array<{label: string, board: number[][]}>} An array containing three distinct pattern objects.
 */
const classicStraightPatterns = () => {
  const patterns = [];

  // Horizontal Line Pattern (e.g., middle row)
  const horizontalBoard = createEmptyBoard();
  for (let j = 0; j < 5; j++) {
    horizontalBoard[2][j] = 1; // Marks the 3rd row (index 2)
  }
  patterns.push({ label: 'Horizontal Line', board: horizontalBoard });

  // Vertical Line Pattern (e.g., middle column)
  const verticalBoard = createEmptyBoard();
  for (let i = 0; i < 5; i++) {
    verticalBoard[i][2] = 1; // Marks the 3rd column (index 2)
  }
  patterns.push({ label: 'Vertical Line', board: verticalBoard });

  // Diagonal Line Pattern (Top-Left to Bottom-Right)
  const diagonalTLBRBoard = createEmptyBoard();
  for (let i = 0; i < 5; i++) {
    diagonalTLBRBoard[i][i] = 1; // Marks cells where row index equals column index
  }
  patterns.push({ label: 'Diagonal Line (TL-BR)', board: diagonalTLBRBoard });

  // Optional: Add the other diagonal (Top-Right to Bottom-Left) if needed for "Any Straight Line"
  const diagonalTRBLBoard = createEmptyBoard();
  for (let i = 0; i < 5; i++) {
    diagonalTRBLBoard[i][4 - i] = 1; // Marks cells from top-right to bottom-left
  }
  patterns.push({ label: 'Diagonal Line (TR-BL)', board: diagonalTRBLBoard });


  return patterns;
};

/**
 * Generates the Four Corners bingo pattern.
 * @returns {Array<{label: string, board: number[][]}>} An array containing one pattern object.
 */
const fourCornersPattern = () => {
  const board = createEmptyBoard();
  board[0][0] = 1; board[0][4] = 1;
  board[4][0] = 1; board[4][4] = 1;
  return [{ label: 'Four Corners', board }];
};

const postageStampPatterns = () => { // This is the revised single pattern from user
  const board = createEmptyBoard();
  board[2][1] = 1; board[2][2] = 1;
  board[3][1] = 1; board[3][2] = 1;
  return [{ label: 'Stamp', board }]; // User's label
};

const pictureFramePattern = () => {
  const board = createEmptyBoard();
  for (let i = 0; i < 5; i++) {
    board[0][i] = 1; board[4][i] = 1;
    board[i][0] = 1; board[i][4] = 1;
  }
  return [{ label: 'Picture Frame', board }];
};

const blackoutPattern = () => {
  const board = createEmptyBoard();
  for (let i = 0; i < 5; i++) {
    for (let j = 0; j < 5; j++) {
      board[i][j] = 1;
    }
  }
  return [{ label: 'Blackout', board }];
};

// --- Adult-Themed & Letter Patterns (from user input) ---

const dickPatterns = () => {
  const p1 = createEmptyBoard();
  p1[0][2] = 1; p1[1][2] = 1; p1[2][2] = 1; p1[3][0] = 1; p1[3][1] = 1; p1[3][2] = 1; p1[3][3] = 1; p1[3][4] = 1;
  p1[4][0] = 1; p1[4][1] = 1; p1[4][3] = 1; p1[4][4] = 1;
  return [{ label: 'Dick Pick', board: p1 }]; // User's label
};

const sadFacePattern = () => {
  const board = createEmptyBoard();
  board[1][1] = 1; board[1][3] = 1;
  board[4][0] = 1; board[3][0] = 1; board[3][1] = 1; board[3][2] = 1; board[3][3] = 1; board[3][4] = 1; board[4][4] = 1;
  return [{ label: 'Sad Face', board }];
};

const fLetterPattern = () => {
  const board = createEmptyBoard();
  for (let i = 0; i < 5; i++) board[i][0] = 1;
  board[0][1] = 1; board[0][2] = 1; board[0][3] = 1; board[0][4] = 1;
  board[2][1] = 1; board[2][2] = 1; board[2][3] = 1;
  return [{ label: 'Letter F', board }];
};

const uLetterPattern = () => {
  const board = createEmptyBoard();
  for (let i = 0; i < 4; i++) {
    board[i][0] = 1; board[i][4] = 1;
  }
  board[4][0] = 1; board[4][1] = 1; board[4][2] = 1; board[4][3] = 1; board[4][4] = 1;
  return [{ label: 'Letter U', board }];
};

const sLetterPattern = () => {
  const board = createEmptyBoard();
  board[0][1] = 1; board[0][2] = 1; board[0][3] = 1; board[0][4] = 1;
  board[1][0] = 1;
  board[2][1] = 1; board[2][2] = 1; board[2][3] = 1;
  board[3][4] = 1;
  board[4][0] = 1; board[4][1] = 1; board[4][2] = 1; board[4][3] = 1;
  return [{ label: 'Letter S', board }];
};

const eLetterPattern = () => {
  const board = createEmptyBoard();
  for (let i = 0; i < 5; i++) board[i][0] = 1;
  board[0][1] = 1; board[0][2] = 1; board[0][3] = 1; board[0][4] = 1;
  board[2][1] = 1; board[2][2] = 1; board[2][3] = 1;
  board[4][1] = 1; board[4][2] = 1; board[4][3] = 1; board[4][4] = 1;
  return [{ label: 'Letter E', board }];
};

const xLetterPattern = () => {
  const board = createEmptyBoard();
  board[0][0] = 1; board[0][4] = 1;
  board[1][1] = 1; board[1][3] = 1;
  board[2][2] = 1;
  board[3][1] = 1; board[3][3] = 1;
  board[4][0] = 1; board[4][4] = 1;
  return [{ label: 'Letter X', board }];
};


// --- Updated BINGO_ROUNDS Definitions ---
export const BINGO_ROUNDS = [
  {
    id: 'round1',
    name: 'Dick Pic',
    description: "To Win: Picture Frame or 'Phallic Symbol' Patterns",
    patterns: [...pictureFramePattern(), ...dickPatterns()],
    displayType: 'multiple',
  },
  {
    id: 'round2',
    name: 'That Baby Dont Look Like Me',
    description: 'To Win: Postage Stamp, Four Corners, or Sad Face Patterns',
    patterns: [...postageStampPatterns(), ...fourCornersPattern(), ...sadFacePattern()],
    displayType: 'multiple',
  },
  {
    id: 'round3',
    name: 'Fuck You',
    description: 'To Win: F or U Letter Patterns',
    patterns: [...fLetterPattern(), ...uLetterPattern()],
    displayType: 'multiple',
  },
  {
    id: 'round4',
    name: 'Fuck Me',
    description: 'To Win: S, E or X Letter Patterns',
    patterns: [...sLetterPattern(), ...eLetterPattern(), ...xLetterPattern()],
    displayType: 'multiple',
  },
  {
    id: 'round5',
    name: 'Roofied',
    description: 'To Win: "BLACKOUT" All 25 Squares',
    patterns: blackoutPattern(),
    displayType: 'single',
  },
  {
    id: 'round6',
    name: 'Basic Bitch',
    description: 'Normal Classic Bingo Everyone Loves :) Any Straight Line Wins!',
    patterns: classicStraightPatterns(), // Now returns multiple patterns
    displayType: 'multiple', // Changed to 'multiple' as it now returns multiple patterns
  },
];
